import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import useAuth from '../../auth/useAuth';
import { useSoundStatus } from '../../context/SoundStatusContext';

import Layout from '../UI/introGameLayout/layout';

import BackgroundImage from './images/game-background.jpg';

import GameProcessSound from './sounds/game-process.mp3';

import ClickSound from './sounds/click.mp3';
import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';
import UpdateBalanceSheetSound from './sounds/update-balance-sheet.mp3';

import {
  generalInfoQuery,
  scenarioLevelsQuery,
  getTransactionDecisions,
  updateGameSession,
  postDecisionsBulk,
} from './api';

import {
  transactionsAnswerOptions,
  gameQuestionsLimit
} from './data';

import {
  validateDecision,
  calculateScore,
  saveSessionToStorage,
  loadSessionFromStorage,
  clearSessionFromStorage,
  formatDecisionsForBulkPost,
} from './validation';

import { calculateTimeInSeconds } from '../../utils';

import Intro from '../UI/game/intro';

import TransactionsDecisions from '../UI/thePrinciples/transactionsDecisions';

import FinancialStatement from '../UI/thePrinciples/financialStatement';

import './styles.css';
import ExpenseRecognitionModal from '../UI/thePrinciples/transactionsDecisions/modal';
import Modal from '../UI/modal';
// import BalanceSheet from '../UI/thePrinciples/balanceSheet';
import BalanceSheet from '../UI/thePrinciples/financialStatement/balanceSheet';
import EmmaMessageResults from '../UI/thePrinciples/emmaMessagesResults';

export default function ThePrinciples({ levelNumber, scenarioLevels, scenarioId, answerData }) {
  const gameProcessEffect = useRef();

  const clickEffect = useRef();
  const answerCorrectEffect = useRef();
  const answerWrongEffect = useRef();
  const updateBalanceSheetEffect = useRef();

  const postInFlightRef = useRef(null);
  
  useEffect(() => {
    const gameProcessAudio = new Audio(GameProcessSound);
    gameProcessAudio.preload = 'auto';  
    gameProcessEffect.current = gameProcessAudio;

    const clickAudio = new Audio(ClickSound);  
    clickAudio.preload = 'auto';  
    clickEffect.current = clickAudio;

    const answerCorrectAudio = new Audio(AnswerCorrectSound);  
    answerCorrectAudio.preload = 'auto';  
    answerCorrectEffect.current = answerCorrectAudio;
    
    const answerWrongAudio = new Audio(AnswerWrongSound);  
    answerWrongAudio.preload = 'auto';  
    answerWrongEffect.current = answerWrongAudio;

    const updateBalanceSheetAudio = new Audio(UpdateBalanceSheetSound);  
    updateBalanceSheetAudio.preload = 'auto';  
    updateBalanceSheetEffect.current = updateBalanceSheetAudio;
    updateBalanceSheetEffect.current.volume = 0.3;
  
    return () => {
      gameProcessAudio.pause();
      clickAudio.pause();
      answerCorrectAudio.pause();
      answerWrongAudio.pause();
      updateBalanceSheetAudio.pause();
    };
  }, []);

  const { soundStatus } = useSoundStatus();

  useEffect(() => {
    const playClickSound = () => {
      if (soundStatus === 'enabled') {
        clickEffect.current.play().catch(e => console.error('Error playing click sound:', e));
      }
    };

    document.addEventListener('click', playClickSound);

    return () => {
      document.removeEventListener('click', playClickSound);
    };
  }, [soundStatus]);

  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const transactionToShow = Number(searchParams.get('transaction'));
  const transactionDecisionToShowStatus = searchParams.get('status');
  const selectedAnswerOptionToShow = searchParams.get('answer');

  const { getUser, getCurrentThePrinciplesGameId } = useAuth();

  const user = getUser();
  
  const gameId = getCurrentThePrinciplesGameId();

  const [isBalanceSheetVisible, setIsBalanceSheetVisible] = useState(false);
  const [isIncomeStatementVisible, setIsIncomeStatementVisible] = useState(false);
  const [shouldBalanceSheetFadeOut, setShouldBalanceSheetFadeOut] = useState(false);
  const [isWellDoneNextButtonLoading, setIsWellDoneNextButtonLoading] = useState(false);
  const [shouldIncomeStatementFadeOut, setShouldIncomeStatementFadeOut] = useState(false);
  const [selectedAnswerOption, setSelectedAnswerOption] = useState(null);
  const [answerStatus, setAnswerStatus] = useState(null);
  const [answerPoints, setAnswerPoints] = useState(null);
  const [isExplanationVisible, setIsExplanationVisible] = useState(false);
  const [explanationContent, setExplanationContent] = useState(null);
  const [correctlyAnsweredTransactions, setCorrectlyAnsweredTransactions] = useState([]);
  const [isExpenseRecognitionModalVisible, setIsExpenseRecognitionModalVisible] = useState(false);
  const [isWellDoneModalVisible, setIsWellDoneModalVisible] = useState(false);
  const [isTransactionsDecisionsVisible, setIsTransactionsDecisionsVisible] = useState(true);
  const [isEmmaResultsVisible, setIsEmmaResultsVisible] = useState(false);

  const [isSubmitButtonShown, setIsSubmitButtonShown] = useState(true);
  const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false);

  const [revenuesTotal, setRevenuesTotal] = useState(0);
  const [expensesTotal, setExpensesTotal] = useState(0);
  
  const [levelTransactions, setLevelTransactions] = useState([]);
  const [activeTransaction, setActiveTransaction] = useState(0);

  const [assets, setAssets] = useState(0);
  const [liabilities, setLiabilities] = useState(0);
  const [equity, setEquity] = useState(0);

  const [assetsChildren, setAssetsChildren] = useState([]);
  const [liabilitiesChildren, setLiabilitiesChildren] = useState([]);
  const [equityChildren, setEquityChildren] = useState([]);
  
  const assetsChildrenRef = useRef(assetsChildren);
  const liabilitiesChildrenRef = useRef(liabilitiesChildren);
  const equityChildrenRef = useRef(equityChildren);

  const [revenuesChildren, setRevenuesChildren] = useState([]);
  const [expensesChildren, setExpensesChildren] = useState([]);
  
  const revenuesChildrenRef = useRef(revenuesChildren);
  const expensesChildrenRef = useRef(expensesChildren);

  const [scenarioLevel, setScenarioLevel] = useState(null);
  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  const referenceItems = answerData?.find(entry => entry.reference_items)?.reference_items || [];

  const getItemWithParent = (itemId, parentId) => {
    const item = referenceItems.find(ri => ri.id === itemId);
    const parent = referenceItems.find(ri => ri.id === parentId);
    if (!item) return null;
    return {
      id: item.id,
      name: item.name,
      parent_objects: parent ? [{ name: parent.name }] : []
    };
  };

  const [questionTries, setQuestionTries] = useState({});
  const [allDecisions, setAllDecisions] = useState([]);

  const [isGameBelongsToTournament, setIsGameBelongsToTournament] = useState(null);

  const [leftIntroTitle, setLeftIntroTitle] = useState(null);
  const [leftIntroSubtitle, setLeftIntroSubtitle] = useState(null);
  const [leftIntroDescription, setLeftIntroDescription] = useState(null);

  const [rightIntroTitle, setRightIntroTitle] = useState(null);
  const [rightIntroSubtitle, setRightIntroSubtitle] = useState(null);
  const [rightIntroDescription, setRightIntroDescription] = useState(null);

  const [isIntroContentCentered, setIsIntroContentCentered] = useState(false);
  const [isIntroSubtitleContentReversed, setIsIntroSubtitleContentReversed] = useState(false);
  const { data: generalInfo } = useQuery(generalInfoQuery());
  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  useEffect(() => {
    if(gameId && scenarioLevel && games) {
      const currentGame = games.find(game => game.id === gameId);
      setIsGameBelongsToTournament(currentGame?.tournament_ids?.length > 0);
      // The Principles intro slide is intentionally rendered without text —
      // the scenario narrative now lives on the dedicated APC intro page and the
      // other levels don't need a narrative here either.
    }
  }, [games, gameId, levelNumber, scenarioLevel]);

  const { data: transactionDecisions } = useQuery({
    queryKey: [`transactionDecisionsThePrinciples_${levelNumber}`, { userId: user?.id, gameId: gameId, gameSessionId: currentGameSessionId }],
    queryFn: () => getTransactionDecisions({ gameSessionId: currentGameSessionId }),
    enabled: !!user?.id && !!gameId && !!currentGameSessionId,
    refetchOnWindowFocus: false
  });


  const queryClient = useQueryClient();
  
  const updateGameSessionMutation = useMutation({
    mutationFn: (data) => updateGameSession(data),
  });


  const resetGame = () => {
    setIsIntroVisible(true);

    setTotalScore(0);
    setAnswerStatus(null);
    setSelectedAnswerOption(null);
    setCorrectlyAnsweredTransactions([]);
    setActiveTransaction(0);
    setLevelTransactions([]);
    setTime(0);
    setItemTimeStart(0);
    setIsSubmitButtonShown(true);
    setScenarioLevel(null);

    resetIncomeStatementsBalanceSheet();
  }
  
  const resetIncomeStatementsBalanceSheet = () => {
    setRevenuesTotal(0);
    setExpensesTotal(0);

    revenuesTotalRef.current = 0;
    expensesTotalRef.current = 0;

    setAssets(0);
    setLiabilities(0);
    setEquity(0);

    assetsChildrenRef.current = [];
    liabilitiesChildrenRef.current = [];
    equityChildrenRef.current = [];
    setAssetsChildren([]);
    setLiabilitiesChildren([]);
    setEquityChildren([]);

    revenuesChildrenRef.current = [];
    expensesChildrenRef.current = [];
    setRevenuesChildren([]);
    setExpensesChildren([]);
  }

  useEffect(() => {
    if(games && gamesSessions && gameId && user?.id && !currentGameSessionId) {
      const currentGameTypeSessions = gamesSessions.filter(session => games.find(game => game.id === session.game && game.game_type === 'The Principles'));
      if(gamesSessions.length && currentGameTypeSessions.length) {
        const currentGameSessions = currentGameTypeSessions.filter(session => session.user === user.id && session.game === gameId);

        if (currentGameSessions.length) {
          const currentNotCompletedGameSession = currentGameSessions.find(session => session.user === user.id && session.game === gameId && !session.completed);
          if(currentNotCompletedGameSession) {
            setCurrentGameSessionId(currentNotCompletedGameSession.id);
          } else {
            const completedGameSession = currentGameTypeSessions
              .filter(session => session.user === user.id && session.game === gameId && session.completed)
              .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);

            setCurrentGameSessionId(completedGameSession?.id);
          }
        }
      }
    }
  }, [games, gamesSessions, gameId, user?.id]);

  // Replay guard: if this level is already completed in the resolved
  // session (e.g. the player hit the back button after completing it),
  // skip past the gameplay component and forward them to the next level
  // — or to the summary if this was the last level. The Principles has
  // no points page, so we match the natural post-completion flow used
  // by handleNextPageButtonClick. Fires once per mount via a ref so it
  // can't re-trigger after the player completes this level mid-session.
  const replayGuardCheckedRef = useRef(false);
  useEffect(() => {
    if (replayGuardCheckedRef.current) return;
    if (!gamesSessions || !currentGameSessionId || !scenarioLevels?.length) return;
    const session = gamesSessions.find(s => s.id === currentGameSessionId);
    if (!session) return;
    replayGuardCheckedRef.current = true;
    if (session[`level${levelNumber}`] === 'completed') {
      const isLastLevel = scenarioLevels.length === levelNumber;
      navigate(
        isLastLevel ? '/the-principles/summary' : `/the-principles/level/${levelNumber + 1}/`,
        { replace: true }
      );
    }
  }, [gamesSessions, currentGameSessionId, levelNumber, scenarioLevels, navigate]);

  useEffect(() => {
    if (scenarioLevels?.length) {
      const currentScenarioLevel = scenarioLevels.find(level => level.level === levelNumber);
      if (currentScenarioLevel) {
        setScenarioLevel(currentScenarioLevel);
      }
    }
  }, [scenarioLevels, levelNumber]);

  // Restore from localStorage on mount
  useEffect(() => {
    if (!currentGameSessionId) return;
    const savedSession = loadSessionFromStorage(currentGameSessionId);
    if (savedSession && savedSession.levelNumber === levelNumber) {
      if (savedSession.questionTries) setQuestionTries(savedSession.questionTries);
      if (savedSession.allDecisions) setAllDecisions(savedSession.allDecisions);
      if (savedSession.correctlyAnsweredTransactions) {
        setCorrectlyAnsweredTransactions(savedSession.correctlyAnsweredTransactions);
      }
      if (savedSession.totalScore) setTotalScore(savedSession.totalScore);
      if (savedSession.time) {
        setTime(savedSession.time);
        setItemTimeStart(savedSession.time);
      }
      if (savedSession.balanceSheet) {
        const bs = savedSession.balanceSheet;
        setAssets(bs.assets || 0);
        setLiabilities(bs.liabilities || 0);
        setEquity(bs.equity || 0);
        if (bs.assetsChildren?.length) { setAssetsChildren(bs.assetsChildren); assetsChildrenRef.current = bs.assetsChildren; }
        if (bs.liabilitiesChildren?.length) { setLiabilitiesChildren(bs.liabilitiesChildren); liabilitiesChildrenRef.current = bs.liabilitiesChildren; }
        if (bs.equityChildren?.length) { setEquityChildren(bs.equityChildren); equityChildrenRef.current = bs.equityChildren; }
      }
      if (savedSession.incomeStatement) {
        const is = savedSession.incomeStatement;
        setRevenuesTotal(is.revenuesTotal || 0);
        setExpensesTotal(is.expensesTotal || 0);
        if (is.revenuesChildren?.length) { setRevenuesChildren(is.revenuesChildren); revenuesChildrenRef.current = is.revenuesChildren; }
        if (is.expensesChildren?.length) { setExpensesChildren(is.expensesChildren); expensesChildrenRef.current = is.expensesChildren; }
      }
    }
  }, [currentGameSessionId, levelNumber]);

  useEffect(() => {
    if (scenarioLevel && transactionDecisions && answerData?.length && referenceItems?.length && !isEmmaResultsVisible && !isWellDoneModalVisible) {
      const scenarioLevelTransactions = scenarioLevel.transactions;
      const savedSession = loadSessionFromStorage(currentGameSessionId);
      const savedAnsweredIds = savedSession?.answeredQuestionIds || [];

      const wrongDecisions = transactionDecisions.filter(decision => decision.is_correct === false);
      const localWrongQuestionIds = savedSession?.allDecisions?.filter(d => !d.is_correct).map(d => d.question) || [];
      const transactionIdsWithWrongDecisions = new Set([
        ...wrongDecisions.map(decision => decision.question),
        ...localWrongQuestionIds
      ]);

      const answeredTransactionCount = scenarioLevelTransactions.filter(transaction => {
        return transactionDecisions.find(decision => decision.question === transaction.id && decision.is_correct) || savedAnsweredIds.includes(transaction.id);
      }).length;

      const unansweredTransactions = scenarioLevelTransactions.filter(transaction =>
        !transactionDecisions.find(decision => decision.question === transaction.id && decision.is_correct) && !savedAnsweredIds.includes(transaction.id)
      );

      const transactionsWithOrder1or2 = unansweredTransactions.filter(transaction => 
        transaction.order === 1 || transaction.order === 2
      );

      const transactionsWithWrongDecisions = unansweredTransactions.filter(transaction => 
        transactionIdsWithWrongDecisions.has(transaction.id) && transaction.order !== 1 && transaction.order !== 2
      );

      const otherUnansweredTransactions = unansweredTransactions.filter(transaction => 
        !transactionIdsWithWrongDecisions.has(transaction.id) && transaction.order !== 1 && transaction.order !== 2
      );

      const availableSlots = Math.max(0, gameQuestionsLimit - answeredTransactionCount);
      const slotsAfterOrder1or2 = Math.max(0, availableSlots - transactionsWithOrder1or2.length);
      const additionalSlotsAvailable = Math.max(0, slotsAfterOrder1or2 - transactionsWithWrongDecisions.length);

      const shuffledOtherTransactions = otherUnansweredTransactions.sort(() => 0.5 - Math.random());
      const randomTransactions = shuffledOtherTransactions.slice(0, additionalSlotsAvailable);

      const mixedTransactions = [...transactionsWithWrongDecisions, ...randomTransactions].sort(() => 0.5 - Math.random());

      const finalTransactionsToShow = [...transactionsWithOrder1or2, ...mixedTransactions];

      setLevelTransactions(finalTransactionsToShow);
      
      if(answeredTransactionCount > 0 || transactionToShow) {
        setIsBalanceSheetVisible(true);
        setIsIncomeStatementVisible(true);
        
        if(transactionToShow) {
          setAnswerStatus(transactionDecisionToShowStatus);
          setActiveTransaction(transactionToShow - 1);
          setSelectedAnswerOption(selectedAnswerOptionToShow);
          setIsExplanationVisible(true);
        }

      }
  
      const updatedCorrectlyAnsweredTransactions = [];
      let accumulatedRevenues = 0;
      let accumulatedExpenses = 0;

      for (const transaction of scenarioLevelTransactions) {
        const relatedCorrectDecision = transactionDecisions.find(decision => decision.question === transaction.id && decision.is_correct);
  
        if (relatedCorrectDecision) {
          const isTransactionAdded = updatedCorrectlyAnsweredTransactions.some(item => item.id === transaction.id);
    
          if (!isTransactionAdded) {
            updatedCorrectlyAnsweredTransactions.push({
              id: transaction.id,
              answer: relatedCorrectDecision.selected_option,
              explanation: relatedCorrectDecision.explanation
            });

            setTotalScore(prevTotalScore => prevTotalScore + relatedCorrectDecision.score_points);
            if (relatedCorrectDecision.time_elapsed) {
              const timeInSeconds = calculateTimeInSeconds(relatedCorrectDecision.time_elapsed);
              setTime(prevTime => prevTime + timeInSeconds);
              setItemTimeStart(prevTime => prevTime + timeInSeconds);
            }

            const answer = answerData.find(a => a.transaction_id === transaction.transaction_id);
            const relatedCreditItem = getItemWithParent(answer?.cr[0], answer?.cr[1]);
            const relatedDebitItem = getItemWithParent(answer?.dr[0], answer?.dr[1]);

            balanceSheetChange(relatedCreditItem, 'credit', transaction.value);
            balanceSheetChange(relatedDebitItem, 'debit', transaction.value);

            if (relatedCorrectDecision.selected_option === 'Increase Profits') {
              accumulatedRevenues += relatedCorrectDecision.value;

              const relatedItem = [relatedDebitItem, relatedCreditItem].find(item =>
                item.parent_objects.some(obj => obj.name === 'Revenues')
              );

              if (relatedItem) {
                incomeStatementChange('revenues', relatedItem, relatedCorrectDecision.value);
              }
            } else if (relatedCorrectDecision.selected_option === 'Decrease Profits') {
              accumulatedExpenses -= relatedCorrectDecision.value;

              const relatedItem = [relatedDebitItem, relatedCreditItem].find(item =>
                item.parent_objects.some(obj => obj.name === 'Expenses')
              );

              if (relatedItem) {
                incomeStatementChange('expenses', relatedItem, relatedCorrectDecision.value);
              }
            }
          }
        }
      }
      if (updatedCorrectlyAnsweredTransactions.length) {
        setRevenuesTotal(accumulatedRevenues);
        setExpensesTotal(accumulatedExpenses);

        const retainedEarningsItem = { name: 'Retained Earnings', parent_objects: [{ name: 'Equity' }] };
        balanceSheetChange(retainedEarningsItem, 'credit', accumulatedRevenues + accumulatedExpenses);
      }

      const localCorrectlyAnswered = savedSession?.correctlyAnsweredTransactions || [];
      const mergedCorrectlyAnswered = [
        ...updatedCorrectlyAnsweredTransactions,
        ...localCorrectlyAnswered.filter(lc => !updatedCorrectlyAnsweredTransactions.some(uc => uc.id === lc.id))
      ];
      setCorrectlyAnsweredTransactions(mergedCorrectlyAnswered);

      const firstUnansweredIndex = finalTransactionsToShow.findIndex(t => !mergedCorrectlyAnswered.some(c => c.id === t.id));

      if (firstUnansweredIndex !== -1) {
        // setActiveTransaction(firstUnansweredIndex);
      } else {
        setIsWellDoneModalVisible(true);
      }
    }
  }, [levelNumber, scenarioLevel, transactionDecisions, answerData, referenceItems]);

  // Persist financial statement state to localStorage whenever it changes
  useEffect(() => {
    if (!currentGameSessionId || !correctlyAnsweredTransactions.length) return;
    const savedSession = loadSessionFromStorage(currentGameSessionId);
    if (savedSession) {
      saveSessionToStorage(currentGameSessionId, {
        ...savedSession,
        balanceSheet: {
          assets, liabilities, equity,
          assetsChildren,
          liabilitiesChildren,
          equityChildren
        },
        incomeStatement: {
          revenuesTotal,
          expensesTotal,
          revenuesChildren,
          expensesChildren
        }
      });
    }
  }, [assets, liabilities, equity, assetsChildren, liabilitiesChildren, equityChildren, revenuesTotal, expensesTotal, revenuesChildren, expensesChildren, currentGameSessionId, correctlyAnsweredTransactions]);

  const updateChildrenValues = (item, valueWithSign, categoryChildrenRef, setCategoryChildren) => {
    const existingItemIndex = categoryChildrenRef.current.findIndex(child => child.name === item.name);
  
    if (existingItemIndex !== -1) {
      const updatedChildren = [...categoryChildrenRef.current];
      
      if(item.name === 'Retained Earnings') {
        updatedChildren[existingItemIndex].value = valueWithSign;
      } else {
        updatedChildren[existingItemIndex].value += valueWithSign;
      }
      if (updatedChildren[existingItemIndex].value === 0 && updatedChildren[existingItemIndex].name !== 'Retained Earnings') {
        updatedChildren.splice(existingItemIndex, 1);
      }
      setCategoryChildren(updatedChildren);
      categoryChildrenRef.current = updatedChildren;
    } else {
      const newChildren = [...categoryChildrenRef.current, { name: item.name, value: valueWithSign }];
      const retainedEarningsIndex = categoryChildrenRef.current.findIndex(child => child.name === 'Retained Earnings');

      if (retainedEarningsIndex !== -1) {
        const retainedEarnings = newChildren.splice(retainedEarningsIndex, 1)[0];
        newChildren.push(retainedEarnings);
      }

      setCategoryChildren(newChildren);
      categoryChildrenRef.current = newChildren;
    }
  };

  const revenuesTotalRef = useRef(revenuesTotal);
  const expensesTotalRef = useRef(expensesTotal);

  useEffect(() => {
    revenuesTotalRef.current = revenuesTotal;
  }, [revenuesTotal]);

  useEffect(() => {
    expensesTotalRef.current = expensesTotal;
  }, [expensesTotal]);

  const balanceSheetChange = (item, normal, value, useRefs = false) => {
    const hasParent = (name) => item.parent_objects.some(parent => parent.name === name);

    if (item.name === 'Assets' || hasParent('Assets')) {
      const valueWithSign = normal === 'credit' ? -value : value;
      setAssets(prevAssets => prevAssets + valueWithSign);

      if(levelNumber > 1) {
        updateChildrenValues(item, valueWithSign, assetsChildrenRef, setAssetsChildren);
      }
    } else if (item.name === 'Liabilities' || hasParent('Liabilities')) {
      const valueWithSign = normal === 'debit' ? -value : value;
      setLiabilities(prevLiabilities => prevLiabilities + valueWithSign);

      if(levelNumber > 1) {
        updateChildrenValues(item, valueWithSign, liabilitiesChildrenRef, setLiabilitiesChildren);
      }
    } else if (item.name === 'Equity' || hasParent('Equity')) {
      const valueWithSign = normal === 'debit' ? -value : value;
      
      setEquity(prevEquity => {
        // Only subtract income totals for Retained Earnings, not for other equity items
        if (item.name === 'Retained Earnings') {
          if (useRefs) {
            return prevEquity + valueWithSign - (revenuesTotalRef.current + expensesTotalRef.current);
          } else {
            return prevEquity + valueWithSign - (revenuesTotal + expensesTotal);
          }
        } else {
          // For Owner's Capital and other equity items, just add the value directly
          return prevEquity + valueWithSign;
        }
      });

      if(levelNumber > 1) {
        updateChildrenValues(item, valueWithSign, equityChildrenRef, setEquityChildren);
      }
    }
  };

  const incomeStatementChange = (type, item, value) => {
    if(levelNumber > 1) {
      if(type === 'revenues') {
        updateChildrenValues(item, value, revenuesChildrenRef, setRevenuesChildren);
      } else if(type === 'expenses') {
        updateChildrenValues(item, -value, expensesChildrenRef, setExpensesChildren);
      }
    }
  }
  
  const handleAnswerOptionButtonClick = (option) => {
    setSelectedAnswerOption(option);
    setAnswerStatus(null);
    setIsExplanationVisible(false);
  };

  const handeSubmitButtonClick = () => {
    setIsSubmitButtonLoading(true);
    const questionId = levelTransactions[activeTransaction]?.id;
    const timeElapsed = time - itemTimeStart;
    const timestamp = new Date().toISOString();

    const activeTransactionData = levelTransactions[activeTransaction];
    const transactionId = activeTransactionData?.transaction_id;

    const currentTries = (questionTries[questionId] || 0) + 1;
    setQuestionTries(prev => ({ ...prev, [questionId]: currentTries }));

    const isCorrect = validateDecision(answerData, transactionId, selectedAnswerOption, referenceItems);
    const scorePoints = isCorrect ? calculateScore({ timeElapsedSeconds: timeElapsed, totalTries: currentTries }) : 0;

    // Find explanation from answer data
    const answerEntry = answerData?.find(a => a.transaction_id === transactionId);
    const explanation = answerEntry?.explanation || null;

    // game_time_elapsed = cumulative seconds since game-start (the on-
    // screen timer at submission time). Stored per decision so the bulk
    // POST sends a value that grows monotonically across rows.
    const gameTimeElapsedSeconds = Math.max(1, time);

    // Store decision for batch POST later
    const newDecision = {
      game: gameId,
      game_session: currentGameSessionId,
      question: questionId,
      selected_option: selectedAnswerOption,
      time_elapsed: timeElapsed || 1,
      game_time_elapsed: gameTimeElapsedSeconds,
      timestamp,
      is_correct: isCorrect,
      score_points: scorePoints,
      score_accuracy: isCorrect ? 1 : 0,
      total_tries: currentTries
    };
    setAllDecisions(prev => [...prev, newDecision]);

    // Find transaction value and items for balance sheet updates
    const transactionValue = activeTransactionData?.value || 0;
    const answer = answerData?.find(a => a.transaction_id === transactionId);

    setIsSubmitButtonLoading(false);
    setIsExplanationVisible(true);

    if (isCorrect) {
      answerCorrectEffect.current.play().catch(e => console.log('Error playing answer correct sound:', e));

      const relatedCreditItem = getItemWithParent(answer?.cr[0], answer?.cr[1]);
      const relatedDebitItem = getItemWithParent(answer?.dr[0], answer?.dr[1]);
      if (soundStatus === 'enabled') {
        updateBalanceSheetEffect.current.play().catch(e => console.error('Error playing balance sheet update sound:', e));
      }

      let updatedRevenuesTotal = revenuesTotal;
      let updatedExpensesTotal = expensesTotal;

      if (selectedAnswerOption === 'Increase Profits') {
        setRevenuesTotal(prevRevenuesTotal => {
          updatedRevenuesTotal = prevRevenuesTotal + transactionValue;
          return updatedRevenuesTotal;
        });

        const relatedItem = [relatedDebitItem, relatedCreditItem].find(item =>
          item.parent_objects.some(obj => obj.name === 'Revenues')
        );

        if (relatedItem) {
          incomeStatementChange('revenues', relatedItem, transactionValue);
        }
      } else if (selectedAnswerOption === 'Decrease Profits') {
        setExpensesTotal(prevExpensesTotal => {
          updatedExpensesTotal = prevExpensesTotal - transactionValue;
          return updatedExpensesTotal;
        });

        const relatedItem = [relatedDebitItem, relatedCreditItem].find(item =>
          item.parent_objects.some(obj => obj.name === 'Expenses')
        );

        if (relatedItem) {
          incomeStatementChange('expenses', relatedItem, transactionValue);
        }
      }

      if (!isBalanceSheetVisible && !isIncomeStatementVisible) {
        setIsIncomeStatementVisible(true);
        setTimeout(() => {
          setIsBalanceSheetVisible(true);
          setTimeout(() => {
            balanceSheetChange(relatedCreditItem, 'credit', transactionValue);
            balanceSheetChange(relatedDebitItem, 'debit', transactionValue);

            if (selectedAnswerOption === 'Increase Profits' || selectedAnswerOption === 'Decrease Profits') {
              const retainedEarningsItem = {name: 'Retained Earnings', parent_objects: [{name: 'Equity'}]};
              balanceSheetChange(retainedEarningsItem, 'credit', updatedRevenuesTotal + updatedExpensesTotal);
            }
          }, 1000);
        }, 2000);
      } else {
        setTimeout(() => {
          balanceSheetChange(relatedCreditItem, 'credit', transactionValue);
          balanceSheetChange(relatedDebitItem, 'debit', transactionValue);

          if (selectedAnswerOption === 'Increase Profits' || selectedAnswerOption === 'Decrease Profits') {
            const retainedEarningsItem = {name: 'Retained Earnings', parent_objects: [{name: 'Equity'}]};
            balanceSheetChange(retainedEarningsItem, 'credit', updatedRevenuesTotal + updatedExpensesTotal);
          }
        }, 1500);
      }

      if (explanation) {
        setExplanationContent(<p>{explanation}</p>);
      } else {
        if (selectedAnswerOption === 'Increase Profits') {
          setExplanationContent(<p>Under the <Link to="https://www.investopedia.com/terms/r/revenuerecognition.asp" title="Revenue Recognition" target="_blank">Revenue Recognition</Link> principles, revenue should be recognized when earned (i.e. when goods or services are provided to the customer) and not necessarily when cash is received. Click <b>Next</b> to continue.</p>);
        } else if (selectedAnswerOption === 'Decrease Profits') {
          setExplanationContent(<p>Under the <button onClick={handleExpenseRecognitionClick}>Expense Recognition</button> (i.e Matching) principle, expense should be recognized when incurred (i.e. when goods or services are used or consumed by the company). Click <b>Next</b> to continue.</p>);
        } else if (selectedAnswerOption === 'No Impact') {
          if (explanation?.includes('revenue-recognition')) {
            setExplanationContent(<p>There's no immediate impact on profits, as revenue was not earned (i.e. goods were not delivered or services provided to the customer).</p>);
          } else if (explanation?.includes('expense-recognition')) {
            setExplanationContent(<p>There's no immediate impact on profit, as expenses were not incurred (i.e. goods or services were not used or consumed by the company).</p>);
          } else {
            setExplanationContent(<p>According to the <Link to="https://www.investopedia.com/terms/r/revenuerecognition.asp" title="Revenue" target="_blank">Revenue</Link> and <button onClick={handleExpenseRecognitionClick}>Expense Recognition</button> (Matching) principles, this transaction has no impact on profits since neither revenue has been earned nor expenses incurred.</p>);
          }
        }
      }

      setAnswerStatus('correct');
      setAnswerPoints(scorePoints);
      setCorrectlyAnsweredTransactions([...correctlyAnsweredTransactions, {id: questionId, answer: selectedAnswerOption}]);
      setTotalScore(prevTotalScore => prevTotalScore + scorePoints);
    } else {
      answerWrongEffect.current.play().catch(e => console.log('Error playing answer wrong sound:', e));
      setAnswerStatus('incorrect');

      if (selectedAnswerOption === 'Increase Profits') {
        setExplanationContent(<p>Under <Link to="https://www.investopedia.com/terms/r/revenuerecognition.asp" title="Revenue Recognition" target="_blank">Revenue Recognition</Link> Principles, there's no immediate increase on profits, as revenue was not earned (i.e. goods were not delivered or services provided to the customer).</p>);
      } else if (selectedAnswerOption === 'Decrease Profits') {
        setExplanationContent(<p>Under <button onClick={handleExpenseRecognitionClick}>Expense Recognition</button> Principles, there's no immediate decrease on profit, as expenses were not incurred (i.e. goods or services were not used or consumed by the company).</p>);
      } else if (selectedAnswerOption === 'No Impact') {
        setExplanationContent(<p>According to the <Link to="https://www.investopedia.com/terms/r/revenuerecognition.asp" title="Revenue" target="_blank">Revenue</Link> and <button onClick={handleExpenseRecognitionClick}>Expense Recognition</button> (Matching) principles, this transaction impacts profits.</p>);
      }
    }

    // Save to localStorage
    saveSessionToStorage(currentGameSessionId, {
      gameId,
      levelNumber,
      questionTries: { ...questionTries, [questionId]: currentTries },
      allDecisions: [...allDecisions, newDecision],
      answeredQuestionIds: isCorrect
        ? [...new Set([...(loadSessionFromStorage(currentGameSessionId)?.answeredQuestionIds || []), questionId])]
        : (loadSessionFromStorage(currentGameSessionId)?.answeredQuestionIds || []),
      correctlyAnsweredTransactions: isCorrect
        ? [...correctlyAnsweredTransactions, { id: questionId, answer: selectedAnswerOption }]
        : correctlyAnsweredTransactions,
      totalScore: (loadSessionFromStorage(currentGameSessionId)?.totalScore || 0) + (isCorrect ? scorePoints : 0),
      balanceSheet: {
        assets, liabilities, equity,
        assetsChildren: assetsChildrenRef.current,
        liabilitiesChildren: liabilitiesChildrenRef.current,
        equityChildren: equityChildrenRef.current
      },
      incomeStatement: {
        revenuesTotal: isCorrect && selectedAnswerOption === 'Increase Profits' ? revenuesTotal + transactionValue : revenuesTotal,
        expensesTotal: isCorrect && selectedAnswerOption === 'Decrease Profits' ? expensesTotal - transactionValue : expensesTotal,
        revenuesChildren: revenuesChildrenRef.current,
        expensesChildren: expensesChildrenRef.current
      },
      time
    });

    setIsSubmitButtonShown(false);
  }

  const handleNextButtonClick = () => {
    setIsExplanationVisible(false);
    setSelectedAnswerOption(null);
    setAnswerStatus(null);
    setIsSubmitButtonShown(true);
    setItemTimeStart(time);
    
    if(levelTransactions.length > 1) {  
      const currentTransaction = levelTransactions[activeTransaction];
      
      if(!correctlyAnsweredTransactions.some(correctlyAnsweredTransaction => correctlyAnsweredTransaction.id === currentTransaction?.id)) {
        // Transaction is not answered - move to next
        setActiveTransaction(prevActiveTransaction => {
          const nextIndex = (prevActiveTransaction === levelTransactions.length - 1) ? 0 : prevActiveTransaction + 1;                  
          return nextIndex;
        });
      } else {
        // Transaction is answered - remove it and adjust activeTransaction
        setLevelTransactions(prevTransactions => {
          const newTransactions = prevTransactions.filter(transaction => transaction.id !== currentTransaction.id);
          
          // Adjust activeTransaction after removal
          setActiveTransaction(prevActiveTransaction => {
            if (newTransactions.length === 0) return 0;
            
            // If we removed the last item, go to first
            if (prevActiveTransaction >= newTransactions.length) {
              return 0;
            }
            
            // Otherwise keep the same index (which now points to the next transaction)
            return prevActiveTransaction;
          });
          
          return newTransactions;
        });
      }
    } else {
      if(correctlyAnsweredTransactions.some(correctlyAnsweredTransaction => correctlyAnsweredTransaction.id === levelTransactions[activeTransaction]?.id)) {
        setIsPlaying(false);
        setIsWellDoneModalVisible(true);

        updateGameSessionMutation.mutate({
          id: currentGameSessionId,
          levelNumber,
          status: 'completed',
          isSessionCompleted: scenarioLevels.length === levelNumber,
        });

        postInFlightRef.current = (async () => {
          try {
            const savedSession = loadSessionFromStorage(currentGameSessionId);
            const decisionsToPost = savedSession?.allDecisions || allDecisions;
            if (decisionsToPost.length) {
              const formattedDecisions = formatDecisionsForBulkPost(decisionsToPost, time);
              await postDecisionsBulk(formattedDecisions);
              clearSessionFromStorage(currentGameSessionId);
            }
          } catch (error) {
            console.error('Failed to post decisions:', error);
          }
        })();
      }
    }
  };

  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {  
    if (isPlaying && soundStatus === 'enabled') {
      gameProcessEffect.current.volume = 0.25;
      gameProcessEffect.current.loop = true;
      gameProcessEffect.current.currentTime = 0;
      gameProcessEffect.current.play().catch(e => console.log('Error playing game process sound:', e));
    } else {
      gameProcessEffect.current.pause();
    }
  
    return () => {
      gameProcessEffect.current.pause();
    };
  }, [isPlaying, soundStatus]);

  const [time, setTime] = useState(0);
  const [itemTimeStart, setItemTimeStart] = useState(0);
  const timeRef = useRef(time);
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    if (!isPlaying) return;
    const timerInterval = setInterval(() => {
      const newTime = timeRef.current + 1;
      setTime(newTime);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isPlaying]);

  const [totalScore, setTotalScore] = useState(0);
  const [isIntroVisible, setIsIntroVisible] = useState(true);

  const handleIntroClose = () => {
    setIsIntroVisible(false);
  }

  useEffect(() => {
    if(!isIntroVisible && !isWellDoneModalVisible 
      && (levelTransactions?.length && correctlyAnsweredTransactions?.length !== levelTransactions?.length)) {
      setIsPlaying(true);
    }
  }, [isIntroVisible, isWellDoneModalVisible, levelTransactions, correctlyAnsweredTransactions]);
  
  const handleExpenseRecognitionClick = () => {
    setIsExpenseRecognitionModalVisible(true);
  };
  
  const handleExpenseRecognitionModalCloseButtonClick = () => {
    setIsExpenseRecognitionModalVisible(false);
  }

  // Wait for the completion POST (if still in flight), then retry once if
  // localStorage still holds decisions — i.e. the completion POST didn't succeed.
  const ensureDecisionsPosted = async () => {
    if (postInFlightRef.current) {
      await postInFlightRef.current;
    }
    const savedSession = loadSessionFromStorage(currentGameSessionId);
    if (savedSession?.allDecisions?.length) {
      try {
        const formattedDecisions = formatDecisionsForBulkPost(savedSession.allDecisions, time);
        await postDecisionsBulk(formattedDecisions);
        clearSessionFromStorage(currentGameSessionId);
      } catch (error) {
        console.error('Retry post decisions failed:', error);
      }
    }
  };

  const handleNextLevelButtonClick = async () => {
    let redirectUrl = `/the-principles/level/${levelNumber + 1}`;

    // remove the Emma thanks screen from the last level for now
    if(scenarioLevels?.length === levelNumber) {
      redirectUrl = '/the-principles/summary';

      await ensureDecisionsPosted();
      // setShouldIncomeStatementFadeOut(true);
      // setShouldBalanceSheetFadeOut(true);
      // setIsWellDoneNextButtonLoading(true);

      // setTimeout(() => {
      //   setIsIncomeStatementVisible(false);
      //   setIsBalanceSheetVisible(false);
      //   resetIncomeStatementsBalanceSheet();

      //   setTimeout(() => {
      //     setShouldIncomeStatementFadeOut(false);
      //     setShouldBalanceSheetFadeOut(false);

      //     const fetchUpdatedTransactions = async () => {
      //       try {
      //         await queryClient.refetchQueries([`transactionDecisionsThePrinciples_${levelNumber}`]);
      //         setIsWellDoneModalVisible(false);
      //         setIsWellDoneNextButtonLoading(false);
              
      //         setIsTransactionsDecisionsVisible(false);
      //         setIsEmmaResultsVisible(true);
      //         const freshTransactionDecisions = queryClient.getQueryData([`transactionDecisionsThePrinciples_${levelNumber}`, { userId: user?.id, gameId: gameId, gameSessionId: currentGameSessionId }]);
                            
      //         if (!freshTransactionDecisions) {
      //           console.error('Failed to get updated transaction decisions');
      //           return;
      //         }

      //         let accumulatedRevenues = 0;
      //         let accumulatedExpenses = 0;

      //         for (const scenarioLevel of scenarioLevels) {
      //           const scenarioLevelTransactions = scenarioLevel.transactions;
                
      //           for (const transaction of scenarioLevelTransactions) {
      //             const relatedCorrectDecision = freshTransactionDecisions.find(decision => decision.question === transaction.id && decision.is_correct);

      //             const relatedPrincipleQuestion = principlesQuestions.find(question => question.id === relatedCorrectDecision?.question);
      //             const relatedTransaction = transactions.find(transaction => transaction.id === relatedPrincipleQuestion?.transaction);
      //             if(relatedTransaction) {
      //               const relatedCreditItem = items.find(item => item.id === relatedTransaction?.credit);
      //               const relatedDebitItem = items.find(item => item.id === relatedTransaction?.debit);

      //               balanceSheetChange(relatedCreditItem, 'credit', relatedTransaction.value, true);
      //               balanceSheetChange(relatedDebitItem, 'debit', relatedTransaction.value, true);

      //               if (relatedCorrectDecision.selected_option === 'Increase Profits') {
      //                 accumulatedRevenues += relatedCorrectDecision.value;

      //                 const relatedItem = [relatedDebitItem, relatedCreditItem].find(item =>
      //                   item.parent_objects.some(obj => obj.name === 'Revenues')
      //                 );

      //                 if (relatedItem) {
      //                   incomeStatementChange('revenues', relatedItem, relatedCorrectDecision.value);
      //                 }
      //               } else if (relatedCorrectDecision.selected_option === 'Decrease Profits') {
      //                 accumulatedExpenses -= relatedCorrectDecision.value;

      //                 const relatedItem = [relatedDebitItem, relatedCreditItem].find(item =>
      //                   item.parent_objects.some(obj => obj.name === 'Expenses')
      //                 );

      //                 if (relatedItem) {
      //                   incomeStatementChange('expenses', relatedItem, relatedCorrectDecision.value);
      //                 }
      //               }
      //             }
      //           }
      //         }

      //         setRevenuesTotal(accumulatedRevenues);
      //         setExpensesTotal(accumulatedExpenses);
              
      //         const retainedEarningsItem = { name: 'Retained Earnings', parent_objects: [{ name: 'Equity' }] };
      //         balanceSheetChange(retainedEarningsItem, 'credit', accumulatedRevenues + accumulatedExpenses, true);

      //         setTimeout(() => {
      //           setIsIncomeStatementVisible(true);

      //           setTimeout(() => {
      //             setIsBalanceSheetVisible(true);
      //           }, 2000);
      //         }, 500);
      //       } catch (error) {
      //         console.error('Error fetching transaction decisions:', error);
      //       }
      //     }

      //     fetchUpdatedTransactions();          
      //   }, 500);
      // }, 200);
    } else {
      console.log('else');
      const hasCompletedLevelTypeBefore = async (levelName, sessionsForGameType) => {
        const scenarioIds = [...new Set(
          sessionsForGameType
            .map(session => {
              const sessionGame = games.find(game => game.id === session.game);
              return sessionGame?.scenario;
            })
            .filter(Boolean)
        )];
        console.log('scenarioIds', scenarioIds);
  
        if (scenarioIds.length === 0) return false;
  
        const allScenarioLevels = [];
    
        for (const scId of scenarioIds) {
          if (scId === scenarioId) {
            allScenarioLevels.push(...scenarioLevels.map(level => ({ ...level, scenarioId: scId })));
            continue;
          }
  
          const levels = await queryClient.fetchQuery({ ...scenarioLevelsQuery({scenarioId: scId}), staleTime: Infinity }).catch(error => {
            console.error(`Error fetching scenario levels ${scId}:`, error);
            return null;
          });
  
          if (levels) {
            allScenarioLevels.push(...levels.map(level => ({ ...level, scenarioId: scId })));
          }
        }
  
        return sessionsForGameType.some(session => {
          const sessionGame = games.find(game => game.id === session.game);
          if (!sessionGame || !sessionGame.scenario) return false;
  
          const matchingLevels = allScenarioLevels.filter(
            scenarioLevel => scenarioLevel.scenarioId === sessionGame.scenario && scenarioLevel.name === levelName
          );
  
          return matchingLevels.some(level => {
            const levelKey = `level${level.level}`;
            return session[levelKey] === 'completed';
          });
        });
      };
      
      const sessionsForGameType = gamesSessions?.filter(
        session =>
          session.user === user?.id &&
          games.find(game => game.id === session.game && game.game_type === 'The Principles')
      );
      const nextScenarioLevel = scenarioLevels?.find(scenarioLevel => scenarioLevel.level === levelNumber + 1);
      
      const hasNeverCompletedThisType = !(await hasCompletedLevelTypeBefore(
        nextScenarioLevel?.name,
        sessionsForGameType
      ));
      console.log('hasNeverCompletedThisType', hasNeverCompletedThisType);

      if (hasNeverCompletedThisType) {
        redirectUrl = '/acc/transition';
      }

      await ensureDecisionsPosted();

      setIsWellDoneModalVisible(false);
      resetGame();
    }
    setIsExplanationVisible(false);

    navigate(redirectUrl);
  }

  const handleEmmaOnViewResultsClick = () => {
    navigate('/the-principles/summary');
  }

  // Tournament completion: retry any pending decisions post and close the tab
  // (these tabs are opened via window.open, so window.close is allowed).
  const handleCloseTabClick = async () => {
    await ensureDecisionsPosted();
    window.close();
  }

  return(
    <Layout
      backgroundImage={BackgroundImage}
      isBackgroundBlurred={true}
      isHeaderVisible={true}
      isFooterVisible={true}
      time={time}
      totalScore={totalScore}
      isAvatarVisible={true}
      isExplanationVisible={isExplanationVisible}
      explanationName={answerStatus === 'incorrect' ? 'Bailey' : 'Benny'}
      answerStatus={answerStatus}
      explanationContent={explanationContent}
      shouldExplanationContentBeParsed={false}
      scoreLabel="Points"
    >
      {
        isIntroVisible && (
          <Intro
            leftTitle={leftIntroTitle}
            leftSubtitle={leftIntroSubtitle}
            leftDescription={leftIntroDescription}
            rightTitle={rightIntroTitle}
            rightSubtitle={rightIntroSubtitle}
            rightDescription={rightIntroDescription}
            onShowingCompleted={handleIntroClose}
            isContentCentered={isIntroContentCentered}
            isSubtitleContentReversed={isIntroSubtitleContentReversed}
          />
      )}
      
      <BalanceSheet
        title="Balance Sheet"
        assets={assets}
        liabilities={liabilities}
        equity={equity}
        assetsChildren={assetsChildren}
        liabilitiesChildren={liabilitiesChildren}
        equityChildren={equityChildren}
        shouldSlideIn={!isBalanceSheetVisible}
        shouldFadeOut={shouldBalanceSheetFadeOut}
      />

      {
        isTransactionsDecisionsVisible && 
          <TransactionsDecisions
            key={activeTransaction}
            transactions={levelTransactions}
            activeTransaction={activeTransaction}
            answerOptions={transactionsAnswerOptions}
            selectedAnswerOption={selectedAnswerOption}
            answerStatus={answerStatus}
            answerPoints={answerPoints}
            transactionToShow={transactionToShow}
            correctlyAnsweredTransactions={correctlyAnsweredTransactions}
            onAnswerOptionClick={handleAnswerOptionButtonClick}
            isSubmitButtonShown={isSubmitButtonShown}
            isSubmitButtonLoading={isSubmitButtonLoading}
            onSubmitButtonClick={handeSubmitButtonClick}
            onNextButtonClick={handleNextButtonClick}
            shouldFirstQuestionBeShown={correctlyAnsweredTransactions?.length < 1 && levelNumber === 1}
          />
      }
      
      {
        isEmmaResultsVisible && 
          <EmmaMessageResults onViewResultsClick={handleEmmaOnViewResultsClick} />
      }

      <FinancialStatement 
        title="Income Statement"
        revenues={revenuesTotal}
        expenses={expensesTotal}
        revenuesChildren={revenuesChildren}
        expensesChildren={expensesChildren}
        shouldSlideIn={!isIncomeStatementVisible}
        shouldFadeOut={shouldIncomeStatementFadeOut}
      />

      {isExpenseRecognitionModalVisible && (
        <ExpenseRecognitionModal
          isModalOverlayVisible={true}
          onCloseButtonClick={handleExpenseRecognitionModalCloseButtonClick}
        />
      )}

      {isWellDoneModalVisible && (
        <Modal
          content={
            <>
              <div className="modal-center vertical">
                <div><span>Well done!</span></div>

                {
                  isGameBelongsToTournament
                    ? (
                        scenarioLevels?.length === levelNumber &&
                          <button onClick={handleCloseTabClick} className="button-blue button-view-summary">Close</button>
                      )
                    : <button onClick={handleNextLevelButtonClick} className={`button-blue button-view-summary ${isWellDoneNextButtonLoading ? 'loading' : ''}`}>
                        {!isWellDoneNextButtonLoading && 'Next'}
                      </button>
                }
              </div>
            </>
          }
          isModalSmall={true}
        />
      )}
    </Layout>
  );
}
